<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import conversationAttachmentsAPI from 'dashboard/api/conversationAttachments';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  kanbanCard: {
    type: Object,
    default: null,
  },
  pipelineId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['update-conversation']);

const { t } = useI18n();

// State
const attachments = ref([]);
const isUploading = ref(false);
const dragOver = ref(false);
const fileInputRef = ref(null);
const loading = ref(false);

// Computed
const totalSize = computed(() => {
  return attachments.value.reduce(
    (total, attachment) => total + attachment.file_size,
    0
  );
});

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / k ** i).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = fileName => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'i-lucide-file-text';
    case 'doc':
    case 'docx':
      return 'i-lucide-file-text';
    case 'xls':
    case 'xlsx':
      return 'i-lucide-file-spreadsheet';
    case 'ppt':
    case 'pptx':
      return 'i-lucide-presentation';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return 'i-lucide-image';
    case 'mp4':
    case 'avi':
    case 'mov':
      return 'i-lucide-video';
    case 'mp3':
    case 'wav':
    case 'ogg':
      return 'i-lucide-music';
    case 'zip':
    case 'rar':
    case '7z':
      return 'i-lucide-archive';
    default:
      return 'i-lucide-file';
  }
};

const getFileTypeColor = fileName => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'text-red-600';
    case 'doc':
    case 'docx':
      return 'text-blue-600';
    case 'xls':
    case 'xlsx':
      return 'text-green-600';
    case 'ppt':
    case 'pptx':
      return 'text-orange-600';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return 'text-purple-600';
    case 'mp4':
    case 'avi':
    case 'mov':
      return 'text-pink-600';
    case 'mp3':
    case 'wav':
    case 'ogg':
      return 'text-indigo-600';
    case 'zip':
    case 'rar':
    case '7z':
      return 'text-yellow-600';
    default:
      return 'text-n-slate-9';
  }
};

const formatDate = dateString => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));

  if (diffInMinutes < 1) return 'agora';
  if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h atrás`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d atrás`;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Mock data for demonstration
const mockAttachments = [
  {
    id: 1,
    file_name: 'proposta_comercial.pdf',
    file_size: 2048576, // 2MB
    file_type: 'application/pdf',
    uploaded_at: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
    uploaded_by: {
      name: 'João Silva',
      avatar_url: null,
    },
    download_url: '#',
  },
  {
    id: 2,
    file_name: 'apresentacao_produto.pptx',
    file_size: 5242880, // 5MB
    file_type:
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    uploaded_at: new Date(Date.now() - 43200000).toISOString(), // 12 hours ago
    uploaded_by: {
      name: 'Maria Santos',
      avatar_url: null,
    },
    download_url: '#',
  },
];

const handleFileSelect = () => {
  fileInputRef.value?.click();
};

const handleFileChange = event => {
  const files = Array.from(event.target.files || []);
  uploadFiles(files);
};

const handleDrop = event => {
  event.preventDefault();
  dragOver.value = false;

  const files = Array.from(event.dataTransfer.files || []);
  uploadFiles(files);
};

const handleDragOver = event => {
  event.preventDefault();
  dragOver.value = true;
};

const handleDragLeave = event => {
  event.preventDefault();
  dragOver.value = false;
};

const uploadFiles = async files => {
  if (files.length === 0 || isUploading.value) return;

  isUploading.value = true;

  try {
    if (files.length === 1) {
      // Single file upload
      const response = await conversationAttachmentsAPI.create(
        props.conversation.id,
        files[0],
        props.pipelineId
      );
      attachments.value.unshift(response.data);
    } else {
      // Bulk upload
      const response = await conversationAttachmentsAPI.bulkUpload(
        props.conversation.id,
        files,
        props.pipelineId
      );
      if (response.data.uploaded) {
        attachments.value.unshift(...response.data.uploaded);
      }
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Some files failed to upload:', response.data.errors);
        // TODO: Show error toast for failed uploads
      }
    }

    // Clear file input
    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }

    // Emit update event
    emit('update-conversation', { ...props.conversation });
  } catch (error) {
    console.error('Error uploading files:', error);
    // TODO: Show error toast
  } finally {
    isUploading.value = false;
  }
};

const deleteAttachment = async attachmentId => {
  const attachmentIndex = attachments.value.findIndex(
    att => att.id === attachmentId
  );
  if (attachmentIndex === -1) return;

  const backupAttachment = attachments.value[attachmentIndex];

  try {
    // Optimistic update
    attachments.value.splice(attachmentIndex, 1);

    await conversationAttachmentsAPI.delete(
      props.conversation.id,
      attachmentId
    );

    // Emit update event
    emit('update-conversation', { ...props.conversation });
  } catch (error) {
    // Revert on error
    attachments.value.splice(attachmentIndex, 0, backupAttachment);
    console.error('Error deleting attachment:', error);
    // TODO: Show error toast
  }
};

const downloadAttachment = async attachment => {
  try {
    const response = await conversationAttachmentsAPI.download(
      props.conversation.id,
      attachment.id
    );
    if (response.data && response.data.url) {
      window.open(response.data.url, '_blank');
    } else {
      // Fallback to file_url if available
      if (attachment.file_url) {
        window.open(attachment.file_url, '_blank');
      }
    }
  } catch (error) {
    console.error('Error downloading attachment:', error);
    // TODO: Show error toast
  }
};

const loadAttachments = async () => {
  loading.value = true;
  try {
    // Se temos um kanbanCard, usar dados específicos do card
    // Senão, usar anexos da conversa filtrados por pipeline
    if (props.kanbanCard) {
      // Para cards específicos, carregar anexos da conversa filtrados por pipeline
      // Os anexos já são específicos por pipeline no backend
      const response = await conversationAttachmentsAPI.get(
        props.conversation.id,
        {
          pipeline_id: props.pipelineId,
        }
      );
      attachments.value = response.data || [];
    } else {
      // Fallback para conversas sem card específico
      const response = await conversationAttachmentsAPI.get(
        props.conversation.id,
        {
          pipeline_id: props.pipelineId,
        }
      );
      attachments.value = response.data || [];
    }
  } catch (error) {
    console.error('Error loading attachments:', error);
    attachments.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadAttachments();
});
</script>

<template>
  <div class="p-6">
    <!-- Upload Area -->
    <div class="mb-6">
      <div
        class="border-2 border-dashed rounded-lg p-8 text-center transition-all"
        :class="
          dragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-n-weak hover:border-n-slate-6 hover:bg-n-solid-1'
        "
        @drop="handleDrop"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
      >
        <div
          class="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
          :class="dragOver ? 'bg-blue-100' : 'bg-n-solid-2'"
        >
          <i
            class="size-8"
            :class="
              dragOver
                ? 'i-lucide-upload text-blue-600'
                : 'i-lucide-paperclip text-n-slate-9'
            "
          />
        </div>

        <h3 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ dragOver ? 'Solte os arquivos aqui' : 'Adicionar anexos' }}
        </h3>

        <p class="text-n-slate-11 mb-4">
          {{
            dragOver
              ? ''
              : 'Arraste e solte arquivos aqui ou clique para selecionar'
          }}
        </p>

        <button
          v-if="!dragOver"
          :disabled="isUploading"
          class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-n-slate-6 disabled:cursor-not-allowed transition-colors flex items-center gap-2 mx-auto"
          @click="handleFileSelect"
        >
          <i v-if="isUploading" class="i-lucide-loader-2 size-4 animate-spin" />
          <i v-else class="i-lucide-plus size-4" />
          {{ isUploading ? 'Enviando...' : 'Selecionar Arquivos' }}
        </button>

        <input
          ref="fileInputRef"
          type="file"
          multiple
          class="hidden"
          @change="handleFileChange"
        />
      </div>

      <!-- File size info -->
      <div
        class="flex items-center justify-between mt-3 text-sm text-n-slate-11"
      >
        <span>Tamanho máximo: 10MB por arquivo</span>
        <span v-if="totalSize > 0">Total: {{ formatFileSize(totalSize) }}</span>
      </div>
    </div>

    <!-- Attachments List -->
    <div class="space-y-3">
      <div v-if="loading" class="flex items-center justify-center py-8">
        <div
          class="animate-spin rounded-full h-6 w-6 border-b-2 border-n-slate-9"
        />
      </div>

      <div v-else-if="!attachments.length" class="text-center py-8">
        <div
          class="w-16 h-16 mx-auto mb-4 bg-n-solid-2 rounded-full flex items-center justify-center"
        >
          <i class="i-lucide-paperclip size-8 text-n-slate-9" />
        </div>
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">Nenhum anexo</h3>
        <p class="text-n-slate-11">
          Adicione documentos, imagens ou outros arquivos relacionados a este
          lead.
        </p>
      </div>

      <div
        v-for="attachment in attachments"
        :key="attachment.id"
        class="group flex items-center gap-4 p-4 bg-n-background border border-n-weak rounded-lg hover:shadow-sm transition-all"
      >
        <!-- File Icon -->
        <div class="flex-shrink-0">
          <i
            :class="[
              getFileIcon(attachment.file_name),
              getFileTypeColor(attachment.file_name),
            ]"
            class="size-8"
          />
        </div>

        <!-- File Info -->
        <div class="flex-1 min-w-0">
          <h4 class="font-medium text-n-slate-12 truncate">
            {{ attachment.file_name }}
          </h4>
          <div class="flex items-center gap-4 mt-1 text-sm text-n-slate-11">
            <span>{{ formatFileSize(attachment.file_size) }}</span>
            <span>•</span>
            <span>{{
              formatDate(attachment.created_at || attachment.uploaded_at)
            }}</span>
            <span>•</span>
            <span>por
              {{ attachment.user?.name || attachment.uploaded_by?.name }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex-shrink-0 flex items-center gap-2">
          <button
            class="p-2 text-n-slate-9 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
            title="Visualizar arquivo"
            @click="() => {}"
          >
            <i class="i-lucide-eye size-4" />
          </button>

          <button
            class="p-2 text-n-slate-9 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Baixar arquivo"
            @click="downloadAttachment(attachment)"
          >
            <i class="i-lucide-download size-4" />
          </button>

          <button
            class="p-2 text-n-slate-9 hover:text-red-600 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
            title="Excluir anexo"
            @click="deleteAttachment(attachment.id)"
          >
            <i class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Statistics -->
    <div v-if="attachments.length > 0" class="mt-6 pt-6 border-t border-n-weak">
      <div class="grid grid-cols-2 gap-4 text-center">
        <div class="p-3 bg-blue-50 rounded-lg">
          <div class="text-lg font-semibold text-blue-600">
            {{ attachments.length }}
          </div>
          <div class="text-sm text-blue-800">Arquivos</div>
        </div>

        <div class="p-3 bg-purple-50 rounded-lg">
          <div class="text-lg font-semibold text-purple-600">
            {{ formatFileSize(totalSize) }}
          </div>
          <div class="text-sm text-purple-800">Espaço usado</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Upload area animation */
.border-dashed {
  transition: all 0.2s ease-in-out;
}

/* File type icons */
.text-red-600 {
  color: #dc2626;
}
.text-blue-600 {
  color: #2563eb;
}
.text-green-600 {
  color: #16a34a;
}
.text-orange-600 {
  color: #ea580c;
}
.text-purple-600 {
  color: #9333ea;
}
.text-pink-600 {
  color: #db2777;
}
.text-indigo-600 {
  color: #4f46e5;
}
.text-yellow-600 {
  color: #ca8a04;
}
</style>
